/**
 * Ende-zu-Ende-Verschluesselung. Laeuft ausschliesslich im Browser.
 *
 * Aufbau:
 *   Master-Passphrase --Argon2id--> Master-Key (AES-256)
 *   Master-Key verschluesselt den privaten RSA-Schluessel des Nutzers.
 *   Jeder Eintrag hat einen zufaelligen Item-Key (AES-256-GCM).
 *   Der Item-Key wird pro Berechtigtem einzeln mit dessen RSA-Public-Key verpackt.
 *
 * Der Server sieht davon nur: Public Keys, verpackte Item-Keys und Geheimtext.
 */
import { argon2id } from 'hash-wasm';
import type { EncryptedBlob, SecretPayload, VaultKeyBundle } from '@shared/types';
import { EMPTY_SECRET } from '@shared/types';

/** Argon2id-Parameter. 64 MiB / 3 Durchlaeufe ist der uebliche interaktive Kompromiss. */
export const KDF_PARAMS = {
  memoryKiB: 65_536,
  iterations: 3,
  parallelism: 1,
} as const;

const RSA_ALG: RsaHashedKeyGenParams = {
  name: 'RSA-OAEP',
  modulusLength: 3072,
  publicExponent: new Uint8Array([1, 0, 1]),
  hash: 'SHA-256',
};

// ---------------------------------------------------------------- base64

/** Byte-Puffer, der garantiert auf einem echten ArrayBuffer sitzt - WebCrypto verlangt das. */
export type Bytes = Uint8Array<ArrayBuffer>;

export function toB64(bytes: ArrayBuffer | Uint8Array): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let out = '';
  const chunk = 0x8000;
  for (let i = 0; i < view.length; i += chunk) {
    out += String.fromCharCode(...view.subarray(i, i + chunk));
  }
  return btoa(out);
}

export function fromB64(value: string): Bytes {
  const bin = atob(value);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

function randomBytes(length: number): Bytes {
  return crypto.getRandomValues(new Uint8Array(length));
}

// ---------------------------------------------------------------- AES-GCM

async function aesEncrypt(key: CryptoKey, plaintext: Bytes): Promise<EncryptedBlob> {
  const iv = randomBytes(12);
  const data = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext);
  return { iv: toB64(iv), data: toB64(data) };
}

async function aesDecrypt(key: CryptoKey, blob: EncryptedBlob): Promise<Bytes> {
  const plain = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: fromB64(blob.iv) },
    key,
    fromB64(blob.data),
  );
  return new Uint8Array(plain);
}

// ---------------------------------------------------------------- Master-Key

async function deriveMasterKey(
  passphrase: string,
  saltB64: string,
  params: { kdfMemoryKiB: number; kdfIterations: number; kdfParallelism: number },
): Promise<CryptoKey> {
  const raw = (await argon2id({
    password: passphrase.normalize('NFKC'),
    salt: fromB64(saltB64),
    memorySize: params.kdfMemoryKiB,
    iterations: params.kdfIterations,
    parallelism: params.kdfParallelism,
    hashLength: 32,
    outputType: 'binary',
  })) as Bytes;
  return crypto.subtle.importKey('raw', raw, 'AES-GCM', false, ['encrypt', 'decrypt']);
}

export class WrongPassphraseError extends Error {
  constructor() {
    super('Falsche Master-Passphrase.');
  }
}

/** Erzeugt Schluesselpaar und verpackt den privaten Teil mit der Passphrase. */
export async function createVaultKey(
  passphrase: string,
): Promise<{ bundle: VaultKeyBundle; privateKey: CryptoKey }> {
  const saltB64 = toB64(randomBytes(16));
  const masterKey = await deriveMasterKey(passphrase, saltB64, {
    kdfMemoryKiB: KDF_PARAMS.memoryKiB,
    kdfIterations: KDF_PARAMS.iterations,
    kdfParallelism: KDF_PARAMS.parallelism,
  });

  const pair = await crypto.subtle.generateKey(RSA_ALG, true, ['encrypt', 'decrypt']);
  const pkcs8 = new Uint8Array(await crypto.subtle.exportKey('pkcs8', pair.privateKey));
  const spki = await crypto.subtle.exportKey('spki', pair.publicKey);

  const bundle: VaultKeyBundle = {
    publicKey: toB64(spki),
    encryptedPrivateKey: await aesEncrypt(masterKey, pkcs8),
    kdfSalt: saltB64,
    kdfMemoryKiB: KDF_PARAMS.memoryKiB,
    kdfIterations: KDF_PARAMS.iterations,
    kdfParallelism: KDF_PARAMS.parallelism,
  };

  /*
   * Zum Erzeugen musste das Schlüsselpaar auslesbar sein - sonst liesse sich
   * der private Teil nicht verschluesselt ablegen. Weitergegeben wird aber eine
   * nicht auslesbare Fassung: nur die darf spaeter gemerkt werden, weil der
   * Browser ihr Material dann selbst haelt und JavaScript es nie zu Gesicht
   * bekommt.
   */
  const privateKey = await crypto.subtle.importKey(
    'pkcs8',
    pkcs8,
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['decrypt'],
  );

  pkcs8.fill(0);
  return { bundle, privateKey };
}

/** Entsperrt den privaten Schluessel. Wirft `WrongPassphraseError` bei falscher Eingabe. */
export async function unlockVaultKey(
  passphrase: string,
  bundle: VaultKeyBundle,
): Promise<CryptoKey> {
  const masterKey = await deriveMasterKey(passphrase, bundle.kdfSalt, bundle);
  let pkcs8: Bytes;
  try {
    // Schlaegt der Auth-Tag fehl, war die Passphrase falsch - anderer Grund ist praktisch ausgeschlossen.
    pkcs8 = await aesDecrypt(masterKey, bundle.encryptedPrivateKey);
  } catch {
    throw new WrongPassphraseError();
  }
  const key = await crypto.subtle.importKey(
    'pkcs8',
    pkcs8,
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['decrypt'],
  );
  pkcs8.fill(0);
  return key;
}

/** Passphrase wechseln, ohne das Schluesselpaar zu tauschen - alle Freigaben bleiben gueltig. */
export async function changePassphrase(
  oldPassphrase: string,
  newPassphrase: string,
  bundle: VaultKeyBundle,
): Promise<VaultKeyBundle> {
  const oldMaster = await deriveMasterKey(oldPassphrase, bundle.kdfSalt, bundle);
  let pkcs8: Bytes;
  try {
    pkcs8 = await aesDecrypt(oldMaster, bundle.encryptedPrivateKey);
  } catch {
    throw new WrongPassphraseError();
  }

  const saltB64 = toB64(randomBytes(16));
  const newMaster = await deriveMasterKey(newPassphrase, saltB64, {
    kdfMemoryKiB: KDF_PARAMS.memoryKiB,
    kdfIterations: KDF_PARAMS.iterations,
    kdfParallelism: KDF_PARAMS.parallelism,
  });
  const encryptedPrivateKey = await aesEncrypt(newMaster, pkcs8);
  pkcs8.fill(0);

  return {
    publicKey: bundle.publicKey,
    encryptedPrivateKey,
    kdfSalt: saltB64,
    kdfMemoryKiB: KDF_PARAMS.memoryKiB,
    kdfIterations: KDF_PARAMS.iterations,
    kdfParallelism: KDF_PARAMS.parallelism,
  };
}

// ---------------------------------------------------------------- Item-Keys

export function importPublicKey(spkiB64: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'spki',
    fromB64(spkiB64),
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['encrypt'],
  );
}

export function generateItemKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, [
    'encrypt',
    'decrypt',
  ]) as Promise<CryptoKey>;
}

export async function wrapItemKey(itemKey: CryptoKey, publicKeySpkiB64: string): Promise<string> {
  const raw = await crypto.subtle.exportKey('raw', itemKey);
  const publicKey = await importPublicKey(publicKeySpkiB64);
  const wrapped = await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, publicKey, raw);
  return toB64(wrapped);
}

export async function unwrapItemKey(wrappedB64: string, privateKey: CryptoKey): Promise<CryptoKey> {
  const raw = await crypto.subtle.decrypt(
    { name: 'RSA-OAEP' },
    privateKey,
    fromB64(wrappedB64),
  );
  return crypto.subtle.importKey('raw', raw, 'AES-GCM', true, ['encrypt', 'decrypt']);
}

/** Verpackt einen Item-Key fuer mehrere Empfaenger auf einmal. */
export async function wrapForAll(
  itemKey: CryptoKey,
  recipients: { uid: string; publicKey: string }[],
): Promise<Record<string, string>> {
  const entries = await Promise.all(
    recipients.map(async (r) => [r.uid, await wrapItemKey(itemKey, r.publicKey)] as const),
  );
  return Object.fromEntries(entries);
}

// ---------------------------------------------------------------- Inhalte

export async function encryptSecret(
  itemKey: CryptoKey,
  payload: SecretPayload,
): Promise<EncryptedBlob> {
  const bytes = new TextEncoder().encode(JSON.stringify(payload)) as Bytes;
  return aesEncrypt(itemKey, bytes);
}

export async function decryptSecret(
  itemKey: CryptoKey,
  blob: EncryptedBlob,
): Promise<SecretPayload> {
  const bytes = await aesDecrypt(itemKey, blob);
  const parsed = JSON.parse(new TextDecoder().decode(bytes)) as Partial<SecretPayload>;
  return { ...EMPTY_SECRET, ...parsed };
}

// ---------------------------------------------------------------- Generator

/**
 * Die Grundalphabete enthalten keine leicht verwechselbaren Zeichen.
 * `ambiguous` kommt nur dazu, wenn der Nutzer die Einschraenkung abwaehlt.
 */
const ALPHABETS = {
  lower: 'abcdefghjkmnpqrstuvwxyz',
  upper: 'ABCDEFGHJKMNPQRSTUVWXYZ',
  digits: '23456789',
  symbols: '!@#$%^&*()-_=+[]{}<>?',
  ambiguous: 'iIlLoO01',
};

export interface PasswordOptions {
  length: number;
  upper: boolean;
  digits: boolean;
  symbols: boolean;
  avoidAmbiguous: boolean;
}

/** Zufallszahl ohne Modulo-Verzerrung. */
function randomIndex(max: number): number {
  const limit = Math.floor(0xffff_ffff / max) * max;
  const buf = new Uint32Array(1);
  let value: number;
  do {
    crypto.getRandomValues(buf);
    value = buf[0]!;
  } while (value >= limit);
  return value % max;
}

export function generatePassword(opts: PasswordOptions): string {
  let pool = ALPHABETS.lower;
  if (opts.upper) pool += ALPHABETS.upper;
  if (opts.digits) pool += ALPHABETS.digits;
  if (opts.symbols) pool += ALPHABETS.symbols;
  if (!opts.avoidAmbiguous) pool += ALPHABETS.ambiguous;

  const chars = [...new Set(pool)];
  return Array.from({ length: Math.max(8, opts.length) }, () => chars[randomIndex(chars.length)]).join('');
}

/** Grobe Entropie-Schaetzung in Bit - reicht als Ampel, ersetzt keine echte Analyse. */
export function estimateBits(value: string): number {
  if (!value) return 0;
  let pool = 0;
  if (/[a-z]/.test(value)) pool += 26;
  if (/[A-Z]/.test(value)) pool += 26;
  if (/\d/.test(value)) pool += 10;
  if (/[^a-zA-Z0-9]/.test(value)) pool += 32;
  const unique = new Set(value).size;
  // Wiederholungen senken den realen Wert - sehr grob ueber den Anteil eindeutiger Zeichen.
  return Math.round(Math.log2(Math.max(pool, 2)) * value.length * (unique / value.length));
}
